'use client';

/**
 * useSecurityLock Hook
 *
 * Shared hook for computing lock state from security props.
 * Used by GlassButton, GlassIconButton, GlossyMenuItem, GlassSwitch, GlassSelect, and GlassTabs.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import type { LockConfig, LockState, SecurityProps } from '@ops-matrix/shared-types';
import { useCallback, useMemo } from 'react';
import { usePermissionOptional } from '../providers/PermissionProvider';
import { useI18n } from './useI18n';

// ============================================================================
// Types
// ============================================================================

export interface UseSecurityLockResult {
  /** Lock state (null if not locked) */
  lockState: LockState | null;

  /** Whether the component is locked */
  isLocked: boolean;

  /** Handler for locked click - calls onLockedClick or default behavior */
  handleLockedClick: () => void;

  /** Tooltip text for locked state */
  lockedTooltipText: string;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useSecurityLock
 *
 * Computes lock state from security props using PermissionProvider context.
 * Returns lock state, isLocked flag, and click handler.
 *
 * @example
 * ```tsx
 * function MyButton({ permission, requiredTier, ...props }) {
 *   const { lockState, isLocked, handleLockedClick } = useSecurityLock({
 *     permission,
 *     requiredTier,
 *   });
 *
 *   return (
 *     <Button
 *       disabled={isLocked}
 *       onClick={isLocked ? handleLockedClick : props.onClick}
 *     />
 *   );
 * }
 * ```
 */
export function useSecurityLock(props: SecurityProps): UseSecurityLockResult {
  const {
    permission,
    anyPermission,
    allPermissions,
    requiredTier,
    requiredLicense,
    anyLicense,
    onLockedClick,
    lockedTooltip,
  } = props;

  const permissionContext = usePermissionOptional();
  const { t } = useI18n();

  // Build lock config only if security props are present
  const lockConfig = useMemo<LockConfig | null>(() => {
    if (!permission && !anyPermission && !allPermissions && !requiredTier && !requiredLicense && !anyLicense) {
      return null;
    }

    return {
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
    };
  }, [permission, anyPermission, allPermissions, requiredTier, requiredLicense, anyLicense]);

  // Get lock state from context
  const lockState = useMemo(() => {
    if (!lockConfig || !permissionContext) {
      return null;
    }
    // Don't show locked state while permissions are loading (UI-only, backend enforces security)
    if (permissionContext.isLoading) {
      return null;
    }
    return permissionContext.getLockState(lockConfig);
  }, [lockConfig, permissionContext]);

  const isLocked = !!lockState;

  // Handle locked click
  const handleLockedClick = useCallback(() => {
    if (onLockedClick) {
      onLockedClick();
    } else if (lockState?.onLockedClick) {
      lockState.onLockedClick();
    }
    // If no handler, do nothing - component just appears disabled
  }, [onLockedClick, lockState]);

  // Compute tooltip text
  const lockedTooltipText = lockedTooltip ?? lockState?.reason ?? t('secure.noAccess');

  return {
    lockState,
    isLocked,
    handleLockedClick,
    lockedTooltipText,
  };
}

/**
 * Check if any security props are present
 * Useful for conditional rendering of lock indicators
 */
export function hasSecurityProps(props: Partial<SecurityProps>): boolean {
  return !!(
    props.permission ||
    props.anyPermission ||
    props.allPermissions ||
    props.requiredTier ||
    props.requiredLicense ||
    props.anyLicense
  );
}

export default useSecurityLock;
